import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Text,
  Button,
  Card,
  CardBody,
  Badge,
  SimpleGrid,
  Spinner,
  Alert,
  AlertIcon,
  useToast,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  useBreakpointValue,
  useDisclosure,
  Divider,
  Image,
  Link as ChakraLink,
  Flex
} from '@chakra-ui/react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { ArrowBackIcon, HamburgerIcon, CloseIcon } from '@chakra-ui/icons';
import { FaVial, FaFlask, FaDollarSign, FaClock, FaTint } from 'react-icons/fa';
import { supabase } from '../supabaseClient';

const LabTestDetail = () => {
  const { shortSlug, testId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  
  const [lab, setLab] = useState(null);
  const [test, setTest] = useState(null);
  const [associations, setAssociations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  
  // Mobile menu state
  const { isOpen: isMobileMenuOpen, onOpen: onMobileMenuOpen, onClose: onMobileMenuClose } = useDisclosure();
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    if (shortSlug && testId) {
      fetchTestDetail();
    }
  }, [shortSlug, testId]);

  const fetchTestDetail = async () => {
    try {
      // First, get the lab by short_slug
      const { data: labData, error: labError } = await supabase
        .from('labs')
        .select('*')
        .eq('short_slug', shortSlug)
        .single();

      if (labError) throw labError;
      if (!labData) {
        toast({
          title: 'Lab not found',
          description: 'The requested lab directory does not exist.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        navigate('/');
        return;
      }

      setLab(labData);

      // Try to fetch as test first, then as panel
      let testData = null;
      let testType = null;
      let labOffering = null;

      // Check if it's a test
      const { data: testResult, error: testError } = await supabase
        .from('tests')
        .select('*')
        .eq('id', testId)
        .single();

      if (testResult && !testError) {
        testData = testResult;
        testType = 'test';

        // Get lab offering details
        const { data: offeringData } = await supabase
          .from('lab_test_offerings')
          .select('*')
          .eq('lab_id', labData.id)
          .eq('test_id', testId)
          .eq('is_offered', true)
          .single();

        labOffering = offeringData;
      } else {
        // Try as panel
        const { data: panelResult, error: panelError } = await supabase
          .from('panels')
          .select('*')
          .eq('id', testId)
          .single();

        if (panelResult && !panelError) {
          testData = panelResult;
          testType = 'panel';

          // Get lab offering details
          const { data: offeringData } = await supabase
            .from('lab_panel_offerings')
            .select('*')
            .eq('lab_id', labData.id)
            .eq('panel_id', testId)
            .eq('is_offered', true)
            .single();

          labOffering = offeringData;
        }
      }

      if (!testData) {
        toast({
          title: 'Test not found',
          description: 'The requested test does not exist or is not offered by this lab.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        navigate(`/lab/${shortSlug}`);
        return;
      }

      // Add lab-specific pricing and codes
      testData.type = testType;
      testData.lab_price = labOffering?.lab_cash_price;
      testData.lab_code = labOffering?.lab_test_code;

      setTest(testData);

      // Fetch associations (for panels)
      if (testType === 'panel') {
        const { data: associationData, error: associationError } = await supabase
          .from('panel_tests')
          .select(`
            tests (*)
          `)
          .eq('panel_id', testId);

        if (!associationError && associationData) {
          setAssociations(associationData.map(item => item.tests));
        }
      }

    } catch (error) {
      console.error('Error fetching test details:', error);
      toast({
        title: 'Error loading test details',
        description: 'Please try again later',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/lab/${shortSlug}`);
  };

  const handleBookTest = () => {
    // Navigate to booking with lab and test context
    navigate(`/patient/booking?lab=${lab.id}&test=${test.id}`);
  };

  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50">
        <Container maxW="1200px" py={8}>
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.500" />
            <Text>Loading test details...</Text>
          </VStack>
        </Container>
      </Box>
    );
  }

  if (!test || !lab) {
    return (
      <Box minH="100vh" bg="gray.50">
        <Container maxW="1200px" py={8}>
          <Alert status="error">
            <AlertIcon />
            Test not found or not available through this lab
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Lab Header */}
      <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200">
        <Container maxW="1200px">
          <Flex 
            h={16} 
            alignItems="center" 
            justifyContent="space-between"
            px={{ base: 4, md: 0 }}
          >
            {/* Lab Logo and Name */}
            <HStack spacing={4}>
              <Button
                leftIcon={<ArrowBackIcon />}
                variant="ghost"
                onClick={handleBack}
                size="sm"
              >
                Back to Directory
              </Button>
              
              {lab.logo_url && (
                <Image 
                  src={lab.logo_url} 
                  alt={`${lab.name} logo`}
                  h={8}
                  objectFit="contain"
                />
              )}
              <Text fontSize="lg" fontWeight="bold" color="gray.800">
                {lab.name}
              </Text>
            </HStack>

            {/* Mobile Menu Toggle */}
            {isMobile && (
              <IconButton
                variant="ghost"
                size="sm"
                icon={<HamburgerIcon />}
                onClick={onMobileMenuOpen}
              />
            )}

            {/* Desktop Navigation */}
            {!isMobile && (
              <HStack spacing={4}>
                <ChakraLink as={RouterLink} to="/" color="blue.600" fontWeight="medium">
                  Home
                </ChakraLink>
                <ChakraLink as={RouterLink} to="/test-directory" color="blue.600" fontWeight="medium">
                  All Tests
                </ChakraLink>
              </HStack>
            )}
          </Flex>
        </Container>
      </Box>

      {/* Mobile Menu Drawer */}
      <Drawer isOpen={isMobileMenuOpen} placement="right" onClose={onMobileMenuClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader borderBottomWidth="1px">
            <HStack justify="space-between">
              <Text>Menu</Text>
              <IconButton
                variant="ghost"
                size="sm"
                icon={<CloseIcon />}
                onClick={onMobileMenuClose}
              />
            </HStack>
          </DrawerHeader>
          <DrawerBody>
            <VStack spacing={4} align="stretch" pt={4}>
              <ChakraLink as={RouterLink} to="/" onClick={onMobileMenuClose}>
                Home
              </ChakraLink>
              <ChakraLink as={RouterLink} to="/test-directory" onClick={onMobileMenuClose}>
                All Tests
              </ChakraLink>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Main Content */}
      <Container maxW="1200px" py={8}>
        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={8}>
          
          {/* Left Column - Test Information */}
          <Box gridColumn={{ base: "1", lg: "1 / 3" }}>
            <VStack spacing={6} align="stretch">
              
              {/* Test Header */}
              <Card bg="white" border="1px" borderColor="gray.200">
                <CardBody p={8}>
                  <VStack align="start" spacing={4}>
                    <HStack justify="space-between" w="100%" align="start">
                      <HStack spacing={4}>
                        <Badge
                          colorScheme={test.type === 'panel' ? 'purple' : 'blue'}
                          px={3}
                          py={1}
                          fontSize="sm"
                          borderRadius="md"
                        >
                          <HStack spacing={1}>
                            <Box as={test.type === 'panel' ? FaFlask : FaVial} />
                            <Text>{test.type === 'panel' ? 'Panel' : 'Individual Test'}</Text>
                          </HStack>
                        </Badge>
                      </HStack>
                    </HStack>

                    <Heading size="xl" color="gray.800" lineHeight="short">
                      {test.name}
                    </Heading>

                    {test.lab_code && (
                      <Text color="gray.600" fontFamily="mono" fontSize="sm">
                        Lab Code: {test.lab_code}
                      </Text>
                    )}

                    {test.description && (
                      <Text color="gray.600" fontSize="lg">
                        {test.description}
                      </Text>
                    )}
                  </VStack>
                </CardBody>
              </Card>

              {/* Clinical Information */}
              {test.clinical_significance && (
                <Card bg="white" border="1px" borderColor="gray.200">
                  <CardBody p={8}>
                    <VStack align="start" spacing={4}>
                      <Heading size="lg" color="gray.800">
                        Clinical Information
                      </Heading>
                      <VStack align="start" spacing={3}>
                        <Text lineHeight="1.8" color="gray.700">
                          {isTextExpanded || test.clinical_significance.length <= 300
                            ? test.clinical_significance
                            : `${test.clinical_significance.substring(0, 300)}...`
                          }
                        </Text>
                        {test.clinical_significance.length > 300 && (
                          <Button
                            variant="link"
                            colorScheme="blue"
                            fontSize="sm"
                            fontWeight="medium"
                            onClick={() => setIsTextExpanded(!isTextExpanded)}
                            p={0}
                          >
                            {isTextExpanded ? 'See less' : 'See more'}
                          </Button>
                        )}
                      </VStack>
                    </VStack>
                  </CardBody>
                </Card>
              )}

              {/* Test Details */}
              <Card bg="white" border="1px" borderColor="gray.200">
                <CardBody p={8}>
                  <VStack align="start" spacing={6}>
                    <Heading size="lg" color="gray.800">
                      Test Details
                    </Heading>
                    
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="100%">
                      {test.specimen_type && (
                        <VStack align="start" spacing={2}>
                          <HStack>
                            <Box as={FaTint} color="blue.500" />
                            <Text fontWeight="semibold" color="gray.700">Specimen Type</Text>
                          </HStack>
                          <Text color="gray.600" pl={6}>{test.specimen_type}</Text>
                        </VStack>
                      )}

                      {test.minimum_volume && (
                        <VStack align="start" spacing={2}>
                          <Text fontWeight="semibold" color="gray.700">Minimum Volume</Text>
                          <Text color="gray.600">{test.minimum_volume}</Text>
                        </VStack>
                      )}

                      {test.alternative_specimen && (
                        <VStack align="start" spacing={2}>
                          <Text fontWeight="semibold" color="gray.700">Alternative Specimen</Text>
                          <Text color="gray.600">{test.alternative_specimen}</Text>
                        </VStack>
                      )}

                      {test.reference_range && (
                        <VStack align="start" spacing={2}>
                          <Text fontWeight="semibold" color="gray.700">Reference Range</Text>
                          <Text color="gray.600">{test.reference_range}</Text>
                        </VStack>
                      )}

                      {test.turnaround_time && (
                        <VStack align="start" spacing={2}>
                          <HStack>
                            <Box as={FaClock} color="orange.500" />
                            <Text fontWeight="semibold" color="gray.700">Turnaround Time</Text>
                          </HStack>
                          <Text color="gray.600" pl={6}>{test.turnaround_time}</Text>
                        </VStack>
                      )}

                      {test.fasting_required && (
                        <VStack align="start" spacing={2}>
                          <Text fontWeight="semibold" color="orange.600">Fasting Required</Text>
                          <Text color="gray.600">Patient must fast before this test</Text>
                        </VStack>
                      )}
                    </SimpleGrid>
                  </VStack>
                </CardBody>
              </Card>

              {/* Associated Tests (for panels) */}
              {test.type === 'panel' && associations.length > 0 && (
                <Card bg="white" border="1px" borderColor="gray.200">
                  <CardBody p={8}>
                    <VStack align="start" spacing={4}>
                      <Heading size="lg" color="gray.800">
                        Tests Included in This Panel
                      </Heading>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} w="100%">
                        {associations.map((associatedTest) => (
                          <Box key={associatedTest.id} p={3} bg="gray.50" borderRadius="md">
                            <Text fontWeight="medium" color="gray.700">
                              {associatedTest.name}
                            </Text>
                            {associatedTest.description && (
                              <Text fontSize="sm" color="gray.600" mt={1}>
                                {associatedTest.description}
                              </Text>
                            )}
                          </Box>
                        ))}
                      </SimpleGrid>
                    </VStack>
                  </CardBody>
                </Card>
              )}

            </VStack>
          </Box>

          {/* Right Column - Book This Test */}
          <Box>
            <Card bg="blue.50" border="2px" borderColor="blue.200" position="sticky" top={4}>
              <CardBody p={8}>
                <VStack spacing={6} align="stretch">
                  <VStack spacing={3} textAlign="center">
                    <Heading size="lg" color="blue.800">
                      Book This Test
                    </Heading>
                    <Text color="blue.700">
                      Schedule your appointment with {lab.name}
                    </Text>
                  </VStack>

                  {(test.lab_price || test.cash_price) && (
                    <VStack spacing={2}>
                      <HStack justify="center">
                        <Box as={FaDollarSign} color="green.500" />
                        <Text fontSize="2xl" fontWeight="bold" color="green.600">
                          ${test.lab_price || test.cash_price}
                        </Text>
                      </HStack>
                      <Text fontSize="sm" color="gray.600" textAlign="center">
                        Cash price through {lab.name}
                      </Text>
                    </VStack>
                  )}

                  <Button
                    colorScheme="blue"
                    size="lg"
                    onClick={handleBookTest}
                    w="100%"
                    py={6}
                    fontSize="lg"
                  >
                    Book Now
                  </Button>

                  <VStack spacing={2} fontSize="sm" color="gray.600" textAlign="center">
                    <Text>
                      <strong>Lab Contact:</strong><br />
                      Phone: {lab.phone || '+1 844-718-7800'}<br />
                      Email: {lab.email || 'contact@athomeblooddraw.com'}
                    </Text>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>
          </Box>

        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default LabTestDetail; 